import { ReactNode } from "react";

import {
    ActionMenu,
    ActionMenuProps,
    Anchor,
    Anchors,
    ApplicationBarHeader,
    ApplicationBarHeaderProps,
    ApplicationBarToolbar,
    PageContent,
    PageLayout,
    Steps,
    StepsProps,
    Tabs,
    TabsProps,
} from "@impero-com/ui";
import * as Sentry from "@sentry/react";

import { assertNever } from "../../utils/obj";

export type TabsPropsTyped = { type: "Tabs" } & TabsProps;
export type StepsPropsTyped = { type: "Steps" } & StepsProps;
export interface AnchorsPropsTyped {
    type: "Anchors";
    anchors: Anchor[];
}

// Avoid components like this in the future:
// you should declare as much as possible via JSX composition instead of complex prop data structures
export type ApplicationBarNavigation =
    | TabsPropsTyped
    | StepsPropsTyped
    | AnchorsPropsTyped;

export interface ApplicationBarProps {
    header: ReactNode;
    variant?: ApplicationBarHeaderProps["variant"];
    navigation?: ApplicationBarNavigation;
    actions?: ActionMenuProps;
    /**
     * Hides the actions while keeping their space, so a route that has no
     * actions doesn't shift the layout. Defaults to visible.
     */
    actionsVisible?: boolean;
    contentClassName?: string;
    children?: ReactNode;
}

export function ApplicationBar({
    header,
    variant,
    navigation,
    actions,
    actionsVisible,
    children,
    contentClassName,
}: ApplicationBarProps) {
    return (
        <PageLayout fullHeight={Boolean(children)}>
            <ApplicationBarHeader variant={variant}>
                {header}
                <ApplicationBarToolbar
                    actions={
                        actions && (
                            <ActionMenu
                                id={actions.id}
                                actions={actions.actions}
                            />
                        )
                    }
                    actionsVisible={actionsVisible}
                    navigation={navigation && <Navigation data={navigation} />}
                    hiddenOnPhone={navigation?.type === "Steps" && !actions}
                />
            </ApplicationBarHeader>
            {children && (
                <PageContent className={contentClassName}>
                    {children}
                </PageContent>
            )}
        </PageLayout>
    );
}

interface NavigationProps {
    data: ApplicationBarNavigation;
}

function Navigation({ data }: NavigationProps) {
    switch (data.type) {
        case "Steps":
            return (
                <Steps
                    currentStepId={data.currentStepId}
                    stepSpecs={data.stepSpecs}
                    limitedWidth={data.limitedWidth}
                />
            );
        case "Tabs":
            return (
                <Tabs
                    currentTabId={data.currentTabId}
                    tabSpecs={data.tabSpecs}
                    className={data.className}
                />
            );
        case "Anchors":
            return (
                <Anchors
                    anchors={data.anchors}
                    onError={Sentry.captureException}
                />
            );
        default:
            return assertNever(data);
    }
}
